import { prettifySalesCategory } from "api-types/builders/salesCategory";
import { SalesCategory } from "common/types/salesCategoryTypesEnum";
import { assertIsNotNullOrUndefined, assertIsNotUndefined } from "common/utils/assertOfType";
import { isExteriorPaletteOptionSet } from "doc/options/paletteOptionSet";
import { PlanLibraryBadge } from "lib/components/builders/plan-library/PlanLibraryBadge";
import { OptionSetSalesCategoryMessage } from "lib/components/builders/plan-library/PlanLibraryMessages";
import { PlanLibraryOptionValue } from "lib/components/builders/plan-library/PlanLibraryOptionValue";
import { planLibraryActions } from "lib/components/builders/plan-library/planLibraryReducer";
import { usePlanLibraryDispatch, usePlanLibrarySelector } from "lib/components/builders/plan-library/planLibraryStore";
import type { PlanLibraryOptionSet } from "lib/components/builders/plan-library/planLibraryTypes";
import { salesCategorySelectItems } from "lib/state/salesCategory";
import type { ChangeEvent } from "react";
import { useState } from "react";
import Input from "ui/forms/Input";
import Select from "ui/forms/Select";
import useAppContext from "ui/hooks/useAppContext";
import styles from "./PlanLibraryModal.module.scss";
import PlanLibraryModalWrapper from "./PlanLibraryModalWrapper";

export default function PlanLibraryNonBoolOptionSetModal() {
  const { setUserNotification } = useAppContext();
  const dispatch = usePlanLibraryDispatch();

  const {
    salesCategoryCustomization,
    onHandleDiscarding,
    overridesToDiscard,
    inheritedOptionSet,
    isExteriorPalette,
    optionSetOverride,
    modalOptionSet,
  } = usePlanLibrarySelector((state) => {
    const modalOptionSet = state.modalOptionSet;
    assertIsNotUndefined(modalOptionSet);

    return {
      salesCategoryCustomization: state.builder.salesConfig?.salesCategoryCustomization,
      onHandleDiscarding: state.onHandleDiscarding,
      overridesToDiscard: state.overridesToDiscard,
      inheritedOptionSet: state.inheritedOptionSets[modalOptionSet.id],
      isExteriorPalette: isExteriorPaletteOptionSet(modalOptionSet),
      optionSetOverride: state.overrides[modalOptionSet.id],
      modalOptionSet,
    };
  });

  const [optionSet, setOptionSet] = useState(modalOptionSet);

  function discardOptionSetPropOverride(key: keyof Omit<PlanLibraryOptionSet, "options">) {
    dispatch(
      planLibraryActions.discardOptionSetPropOverride({
        oldOptionSet: modalOptionSet,
        key,
      }),
    );
    setUserNotification({
      message: `Discard override for ${inheritedOptionSet.name}`,
      messageType: "success",
    });
  }

  assertIsNotNullOrUndefined(optionSet);

  return (
    <PlanLibraryModalWrapper
      title={overridesToDiscard ? "Discard Option Set Overrides" : "Edit Option Set Overrides"}
      inheritedOptionSet={inheritedOptionSet}
      submitEnabled={(() => {
        if (overridesToDiscard) {
          return true;
        }
        if (!optionSet.name || (isExteriorPalette && optionSet.salesCategory !== SalesCategory.DESIGN_PALETTES)) {
          return false;
        }
        return true;
      })()}
      onSubmit={async () => {
        if (overridesToDiscard) {
          onHandleDiscarding?.();
        } else {
          dispatch(
            planLibraryActions.overrideOptionSet({
              oldOptionSet: modalOptionSet,
              newOptionSet: optionSet,
            }),
          );
        }
        dispatch(planLibraryActions.setOverridesToDiscard(null));
        dispatch(planLibraryActions.enableOverrideModal(null));
      }}
    >
      <div className={styles.wrapper}>
        <div className={styles.fieldWrapper}>
          <div className={styles.labelWrapper}>
            <PlanLibraryOptionValue
              field="Name"
              isOverridden={optionSetOverride?.name !== undefined}
              inheritedValue={inheritedOptionSet?.name}
            >
              <label htmlFor="name">Sales Name</label>
            </PlanLibraryOptionValue>

            <span className={styles.optionSetBadgeWrapper}>
              <PlanLibraryBadge
                isOverridden={optionSetOverride?.name !== undefined}
                onClickBadge={() => {
                  dispatch(
                    planLibraryActions.setOverridesToDiscard({
                      overridesToDiscard: { name: optionSetOverride.name },
                      onHandleDiscarding: () => discardOptionSetPropOverride("name"),
                    }),
                  );
                }}
                singleField
              />
            </span>
          </div>
          {isExteriorPalette ? (
            <div className={styles.readOnly}>{optionSet.name}</div>
          ) : (
            <Input
              value={optionSet.name}
              id={optionSet.id}
              name="name"
              type="text"
              onInput={(event: ChangeEvent<HTMLInputElement>): void => {
                const newOptionSet = { ...optionSet };
                newOptionSet[event.target.name] = event.target.value;
                setOptionSet(newOptionSet);
              }}
              validate={() => (optionSet.name.length < 1 ? "Option name cannot be empty." : null)}
            />
          )}
        </div>

        <div className={styles.fieldWrapper}>
          <div className={styles.labelWrapper}>
            <PlanLibraryOptionValue
              field="Builder Option Set ID"
              isOverridden={optionSetOverride?.builderOptionSetCode !== undefined}
              inheritedValue={inheritedOptionSet?.builderOptionSetCode}
            >
              <label htmlFor="name">Builder Option Set Code</label>
            </PlanLibraryOptionValue>

            <span className={styles.optionSetBadgeWrapper}>
              <PlanLibraryBadge
                isOverridden={optionSetOverride?.builderOptionSetCode !== undefined}
                onClickBadge={() => {
                  dispatch(
                    planLibraryActions.setOverridesToDiscard({
                      overridesToDiscard: { builderOptionSetCode: optionSetOverride.builderOptionSetCode },
                      onHandleDiscarding: () => discardOptionSetPropOverride("builderOptionSetCode"),
                    }),
                  );
                }}
                singleField
              />
            </span>
          </div>
          <Input
            value={optionSet.builderOptionSetCode ?? undefined}
            id={optionSet.id}
            name="builderOptionSetCode"
            type="text"
            onInput={(event: ChangeEvent<HTMLInputElement>): void => {
              const newOptionSet = { ...optionSet, builderOptionSetCode: event.target.value };
              setOptionSet(newOptionSet);
            }}
          />
        </div>

        <div className={styles.fieldWrapper}>
          <div className={styles.labelWrapper}>
            <PlanLibraryOptionValue
              field="Sales Category"
              isOverridden={optionSetOverride?.salesCategory !== undefined}
              inheritedValue={inheritedOptionSet?.salesCategory}
            >
              <label htmlFor="salesCategory">Sales Category</label>
            </PlanLibraryOptionValue>

            <span className={styles.optionSetBadgeWrapper}>
              <PlanLibraryBadge
                isOverridden={optionSetOverride?.salesCategory !== undefined}
                onClickBadge={() => {
                  dispatch(
                    planLibraryActions.setOverridesToDiscard({
                      overridesToDiscard: { salesCategory: optionSetOverride.salesCategory },
                      onHandleDiscarding: () => discardOptionSetPropOverride("salesCategory"),
                    }),
                  );
                }}
                singleField
              />
            </span>
          </div>
          {isExteriorPalette ? (
            <div className={styles.readOnly}>{prettifySalesCategory(optionSet.salesCategory, undefined)}</div>
          ) : (
            <Select
              options={salesCategorySelectItems(salesCategoryCustomization)}
              onChange={(newCategory) => setOptionSet({ ...optionSet, salesCategory: newCategory as string })}
              value={optionSet.salesCategory}
            />
          )}
        </div>

        <OptionSetSalesCategoryMessage salesCategory={optionSet.salesCategory} />

        <div className={styles.fieldWrapper}>
          <div className={styles.labelWrapper}>
            <PlanLibraryOptionValue
              field="Base Option"
              isOverridden={optionSetOverride?.defaultValue !== undefined}
              inheritedValue={inheritedOptionSet?.defaultValue}
            >
              <label htmlFor="defaultValue">Select Base Option</label>
            </PlanLibraryOptionValue>

            <span className={styles.optionSetBadgeWrapper}>
              <PlanLibraryBadge
                isOverridden={optionSetOverride?.defaultValue !== undefined}
                onClickBadge={() => {
                  dispatch(
                    planLibraryActions.setOverridesToDiscard({
                      overridesToDiscard: { defaultValue: optionSetOverride.defaultValue },
                      onHandleDiscarding: () => discardOptionSetPropOverride("defaultValue"),
                    }),
                  );
                }}
                singleField
              />
            </span>
          </div>
          {isExteriorPalette ? (
            <div className={styles.readOnly}>
              {optionSet.options?.find(({ id }) => id === optionSet.defaultValue)?.name}
            </div>
          ) : (
            <Select
              onChange={(newDefault) => setOptionSet({ ...optionSet, defaultValue: newDefault as string })}
              options={(optionSet.options ?? []).map(({ name, id }) => ({ label: name, value: id }))}
              value={optionSet.defaultValue}
            />
          )}
        </div>
      </div>
    </PlanLibraryModalWrapper>
  );
}
